import Button from '@rio-cloud/rio-uikit/Button';
import TimePicker from '@rio-cloud/rio-uikit/TimePicker';
import ToggleButton from '@rio-cloud/rio-uikit/ToggleButton';
import isEmpty from 'lodash/fp/isEmpty';
import isUndefined from 'lodash/fp/isUndefined';
import uniq from 'lodash/fp/uniq';
import { FormattedMessage } from 'react-intl';
import { Unit } from '../../common/constants';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import type { Connector } from '../../store/facade/facadeApiV3';
import {
    selectIsAutomatedPlanningMode,
    selectIsWaypointInRange,
    selectWaypointMaxPowerById,
} from '../../store/search/searchSelectors';
import {
    updateWaypointCargo,
    updateWaypointWaitTime,
    type Waypoint,
    waypointChanged,
    WaypointType,
} from '../../store/search/searchSlice';
import {
    selectEmptyWeightField,
    selectMaxFreightTrainWeightField,
} from '../../store/vehicleProfile/vehicleProfileSelectors';

type Props = {
    waypoint: Waypoint;
};

export const getConnectorCurrentAndVoltageByPower = (connectors: Connector[], maxPower: number) => {
    const connector = connectors.find((c: Connector) => c.maxPower === maxPower);
    if (connector) {
        return {
            current: connector.current,
            voltage: connector.voltage,
        };
    }
    return null;
};

export const ExtendedOptions = ({ waypoint }: Props) => {
    const dispatch = useAppDispatch();
    const isAutomated = useAppSelector(selectIsAutomatedPlanningMode);
    const isAutomatedStation = isAutomated && waypoint.type === WaypointType.CHARGING_STATION;

    const selectedPower = useAppSelector(state => selectWaypointMaxPowerById(state, waypoint.id));
    const isWaypointInRange = useAppSelector(state => selectIsWaypointInRange(state, waypoint.id));
    const waypointInRange = isWaypointInRange ?? true;
    const maxFreightTrainWeight = useAppSelector(selectMaxFreightTrainWeightField);
    const emptyWeight = useAppSelector(selectEmptyWeightField);

    const maxCargoWeightInKg =
        maxFreightTrainWeight.value !== undefined && emptyWeight.value !== undefined
            ? maxFreightTrainWeight.value * 1000 - emptyWeight.value
            : undefined;

    const currentTime = waypoint.timeAtStopover ?? '00:00';
    const handleStopoverTimeChange = (time: string) => {
        dispatch(updateWaypointWaitTime({ waypointId: waypoint.id, time }));
    };

    const cargoAtDeparture = waypoint.cargoAtDeparture ?? undefined;
    const handleCargoAtDeparture = (cargo?: number) => {
        if (isUndefined(cargo)) {
            return;
        }
        dispatch(updateWaypointCargo({ waypointId: waypoint.id, cargo }));
    };

    const maxPowerSet: number[] = [];
    waypoint.connectors?.forEach((connector: Connector) => {
        if (connector.maxPower) {
            maxPowerSet.push(connector.maxPower);
        }
    });
    const maxPowerSetOrdered = isAutomated ? [waypoint.maxPower] : uniq(maxPowerSet).toReversed();

    const setSelectedPower = (value: number) => {
        if (waypoint.connectors) {
            const connector = getConnectorCurrentAndVoltageByPower(waypoint.connectors, value);
            if (connector) {
                const { current, voltage } = connector;
                dispatch(
                    waypointChanged({
                        ...waypoint,
                        maxPower: value,
                        current,
                        voltage,
                    } as Waypoint)
                );
            }
        }
    };

    const showUnreachableWarning = !isAutomated && !waypointInRange;

    return (
        <div className={`display-flex flex-column 'margin-top-10' ${showUnreachableWarning && 'padding-top-15'}`}>
            <label>
                <FormattedMessage id='intl-msg:smartRoute.tour.timeAtStopover.label' />
            </label>
            <div className='input-group margin-bottom-5' data-testid='waypoint-timepicker'>
                <TimePicker
                    value={currentTime}
                    onChange={handleStopoverTimeChange}
                    alwaysShowMask={false}
                    showIcon
                    className={`width-100pct ${isAutomatedStation ? 'pointer-events-none' : ''}`}
                    inputProps={{ disabled: isAutomatedStation }}
                />
            </div>

            {waypoint.type === WaypointType.ADDITIONAL_STOP && (
                <>
                    <label>
                        <FormattedMessage id='intl-msg:smartRoute.tour.cargoAtDeparture.label' />
                    </label>
                    <div
                        className={`form-group has-feedback ${
                            maxCargoWeightInKg !== undefined && (cargoAtDeparture ?? 0) > maxCargoWeightInKg
                                ? 'has-error'
                                : ''
                        }`}
                        data-testid='waypoint-cargo-weight'
                    >
                        <div className='input-group'>
                            <input
                                type='text'
                                className='form-control'
                                inputMode='decimal'
                                value={cargoAtDeparture ?? 0}
                                onChange={e => {
                                    const parsed = Number.parseInt(e.target.value, 10);
                                    if (!Number.isNaN(parsed) && parsed >= 0) {
                                        handleCargoAtDeparture(parsed);
                                    }
                                }}
                            />
                            <div className='input-group-addon display-flex align-items-center gap-5'>
                                {maxCargoWeightInKg !== undefined && (cargoAtDeparture ?? 0) > maxCargoWeightInKg && (
                                    <span
                                        className='rioglyph rioglyph-error-sign text-color-danger'
                                        aria-hidden='true'
                                    />
                                )}
                                <span>{Unit.weight}</span>
                            </div>
                        </div>
                        {maxCargoWeightInKg !== undefined && (cargoAtDeparture ?? 0) > maxCargoWeightInKg && (
                            <span className='help-block'>
                                <span>
                                    <FormattedMessage
                                        id='intl-msg:smartRoute.vehicle.cargoWeight.exceeded'
                                        values={{ max: maxCargoWeightInKg }}
                                    />
                                </span>
                            </span>
                        )}
                    </div>
                </>
            )}

            {waypoint.type === WaypointType.CHARGING_STATION && !isEmpty(maxPowerSetOrdered) && (
                <>
                    <label className='padding-top-5'>
                        <FormattedMessage id='intl-msg:smartRoute.vehicle.connectorPower' />
                        {` (${Unit.kW})`}
                    </label>
                    <div className='display-flex gap-10'>
                        {maxPowerSetOrdered.map((value: number) => {
                            return (
                                <ToggleButton
                                    key={value}
                                    bsSize={Button.SM}
                                    active={value === selectedPower}
                                    onClick={() => setSelectedPower(value)}
                                    disabled={isAutomatedStation}
                                >
                                    {value}
                                </ToggleButton>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
